/**
 * Tenant branding bootstrap for the learner / family portal (Phase F2c).
 *
 * Contract identical to the IA + apply providers:
 *   1. Read public branding once on mount (`useTenantBranding(slug)`).
 *   2. Write the BE-returned hex into `--color-primary` / `--color-secondary`
 *      on `document.documentElement`, so every Tailwind class that consumes
 *      those vars (Ziggy accent, bottom-nav active, primary buttons, focus
 *      rings) repaints to the institution's brand.
 *   3. Swap the favicon to the tenant's uploaded mark.
 *   4. Surface the branding (incl. `logo_url`) via context so
 *      `<InstitutionMark>` renders the real logo instead of its placeholder.
 *
 * Graceful fallback: when the tenant hasn't set a colour (null), or on the
 * bare dev host where `slug` is null, the provider is a no-op and the mock
 * palette preset from `useInstitutionTheme` renders. On unmount we restore
 * the previous CSS-var values so a tenant switch (rare, but possible for a
 * learner who studies at two centres) doesn't leak the previous brand.
 */

import * as React from "react";
import { useTenantBranding } from "@/lib/branding/api";
import { TenantBrandingContext, type TenantBrandingContextValue } from "./useTenantBrandingContext";

interface TenantBrandingProviderProps {
  slug: string | null | undefined;
  children: React.ReactNode;
}

export function TenantBrandingProvider({ slug, children }: TenantBrandingProviderProps) {
  const { data: branding, isLoading } = useTenantBranding(slug);

  React.useEffect(() => {
    if (!branding) return;
    const root = document.documentElement;
    const previous = {
      primary: root.style.getPropertyValue("--color-primary"),
      secondary: root.style.getPropertyValue("--color-secondary"),
    };
    if (branding.primary_color) {
      root.style.setProperty("--color-primary", branding.primary_color);
    }
    if (branding.secondary_color) {
      root.style.setProperty("--color-secondary", branding.secondary_color);
    }
    if (branding.favicon_url) {
      const link =
        document.querySelector<HTMLLinkElement>("link[rel='icon']") ??
        Object.assign(document.createElement("link"), { rel: "icon" });
      link.href = branding.favicon_url;
      if (!link.parentNode) document.head.appendChild(link);
    }
    return () => {
      if (previous.primary) {
        root.style.setProperty("--color-primary", previous.primary);
      } else {
        root.style.removeProperty("--color-primary");
      }
      if (previous.secondary) {
        root.style.setProperty("--color-secondary", previous.secondary);
      } else {
        root.style.removeProperty("--color-secondary");
      }
    };
  }, [branding]);

  const value = React.useMemo<TenantBrandingContextValue>(
    () => ({ branding, isLoading }),
    [branding, isLoading],
  );

  return <TenantBrandingContext.Provider value={value}>{children}</TenantBrandingContext.Provider>;
}
